import { useMemo } from "react";
import { useTranslate } from "@refinedev/core";
import { Check, FileText } from "lucide-react";

import { formatDateTime } from "../../i18n/format";
import { WachtlijstDierKaart } from "./WachtlijstDierKaart";
import { WachtlijstAntwoorden } from "./WachtlijstAntwoorden";
import type { Dossier } from "./types";

/**
 * Het dossier van één afstandsaanvraag, zoals de mockup van 26-10-2026.
 *
 * <p><b>De kop beantwoordt drie vragen naast elkaar</b> — wie is het, waarom, en wat doen we ermee —
 * en pas daaronder begint het uitzoekwerk. Een medewerker die een aanvraag oppakt leest ze in die
 * volgorde; ze over tabbladen verdelen zou betekenen dat hij klikt om te weten waar het over gaat.
 *
 * <p><b>Eén dier is één kaart.</b> Eigen voortgang, eigen gegevens, eigen foto's. Bij twee dieren in
 * één aanvraag mag niet uitmaken welke je openklapt, en informatie van de een hoort nooit bij de
 * ander terecht te komen.
 *
 * <p><b>De dossiercontrole is afgeleid, niet opgeslagen.</b> Hij telt wat er is: verplichte gegevens,
 * beantwoorde vragen. Een opgeslagen percentage zou verouderen zodra iemand iets aanvult, en dan
 * staat er een vinkje bij een dossier dat niet meer klopt.
 */

/** De vier stappen van een aanvraag, afgeleid uit de status — er is geen aparte workflowkolom. */
const STAPPEN = ["ontvangen", "beoordeling", "inplannen", "afronden"] as const;

function stapVan(status: string): number {
  switch (status) {
    case "NIEUW":
      return 0;
    case "ACTIE_VEREIST":
    case "IN_BEHANDELING":
    case "COMPLEET":
      return 1;
    case "GEPLAND":
      return 2;
    default:
      return 3;
  }
}

export function WachtlijstDossier({
  dossier, tab, onTab, documentenBlok, notitieBlok, kopBlok, fotosVan, locale, onBeoordeel, bezig,
}: {
  dossier: Dossier;
  tab: string;
  onTab: (t: string) => void;
  /** Het documentenblok krijgt zijn eigen staat; het dossier plaatst hem alleen. */
  documentenBlok: React.ReactNode;
  /** Het invoerveld voor een nieuwe notitie; hetzelfde verhaal. */
  notitieBlok: React.ReactNode;
  /** De kop met de drie kaarten; die doet de mutaties en woont daarom bij het scherm. */
  kopBlok: React.ReactNode;
  /** De foto's van een dier, uit de documenten van deze aanvraag. */
  fotosVan: (dierId: number) => { id: string; naam: string; src: string }[];
  locale: string;
  /** Een antwoord beoordelen; leeg oordeel haalt de beoordeling weer weg. */
  onBeoordeel: (dierId: number, veldSleutel: string, oordeel: string | null) => void;
  bezig: boolean;
}) {
  const t = useTranslate();
  const { regel } = dossier;
  const huidige = stapVan(regel.status);

  const vragen = useMemo(() => {
    let beantwoord = 0;
    let verplicht = 0;
    for (const d of dossier.antwoorden) {
      for (const g of d.groepen) {
        beantwoord += g.beantwoord;
        verplicht += g.verplicht;
      }
    }
    return { beantwoord, verplicht };
  }, [dossier.antwoorden]);

  return (
    <div className="wl-dos">
      {kopBlok}

      <ol className="wl-stap" aria-label={t("wachtlijstDossier.voortgang")}>
        {STAPPEN.map((naam, i) => (
          <li key={naam} className={`wl-stap__item${i < huidige ? " is-klaar" : i === huidige ? " is-nu" : ""}`}>
            <span className="wl-stap__bol" aria-hidden="true">{i < huidige ? <Check size={14} /> : i + 1}</span>
            <span className="wl-stap__tekst">
              {t(`wachtlijstDossier.stap.${naam}`)}
              <small>
                {i === 0 ? formatDateTime(regel.ontvangenOp, locale)
                  : i === huidige ? t("wachtlijstDossier.huidigeStap") : ""}
              </small>
            </span>
          </li>
        ))}
      </ol>

      <nav className="wl-tabs" role="tablist">
        {["dieren", "antwoorden", "documenten", "notities", "historie"].map((x) => (
          <button
            key={x}
            type="button"
            role="tab"
            aria-selected={tab === x}
            className={`wl-tabs__tab${tab === x ? " is-actief" : ""}`}
            onClick={() => onTab(x)}
          >
            {t(`wachtlijstDossier.tab.${x}`)}
            {/* Een aantal naast de naam, zodat je niet hoeft te klikken om te weten of er iets is.
                Alleen waar het getal iets zegt: "Afstandsvragen 1" is nietszeggend. */}
            {x === "dieren" && regel.dieren.length > 0 && (
              <span className="wl-tabs__telling">{regel.dieren.length}</span>
            )}
            {x === "notities" && dossier.notities.length > 0 && (
              <span className="wl-tabs__telling">{dossier.notities.length}</span>
            )}
          </button>
        ))}
      </nav>

      {tab === "dieren" && (
        <div className="wl-dieren">
          <div className="wl-dieren__hoofd">
            {regel.dieren.map((d) => (
              <WachtlijstDierKaart
                key={d.id}
                dier={d}
                antwoorden={dossier.antwoorden.find((a) => a.dierId === d.id)}
                entingen={dossier.entingen.find((e) => e.dierId === d.id)}
                fotos={fotosVan(d.id)}
                locale={locale}
                aantalDieren={regel.dieren.length}
              />
            ))}
          </div>
          <aside className="wl-dos__zij">
            <Dossiercontrole dossier={dossier} vragen={vragen} t={t} />
          </aside>
        </div>
      )}

      {tab === "antwoorden" && (
        <section className="wl-kaart">
          <WachtlijstAntwoorden
            antwoorden={dossier.antwoorden}
            meerdereDieren={regel.dieren.length > 1}
            onBeoordeel={onBeoordeel}
            bezig={bezig}
          />
          <p className="fawkes-muted wl-beoordeel__herkomst">
            {t("wachtlijstDossier.vragenlijstHerkomst")}
          </p>
        </section>
      )}

      {tab === "documenten" && <section className="wl-kaart">{documentenBlok}</section>}

      {tab === "notities" && (
        <section className="wl-kaart">
          <h3>{t("wachtlijstDossier.tab.notities")}</h3>
          {/* Dat de eigenaar dit nooit ziet staat erbij: bij twijfel schrijft niemand iets op. */}
          <p className="fawkes-muted wl-dossier__uitleg">{t("wachtlijstDossier.notitiesUitleg")}</p>
          {notitieBlok}
          {dossier.notities.length === 0 ? (
            <p className="fawkes-muted">{t("wachtlijstDossier.geenNotities")}</p>
          ) : (
            <ul className="wl-notities">
              {dossier.notities.map((n) => (
                <li key={n.id}>
                  <p>{n.tekst}</p>
                  <small>
                    {n.geschrevenDoor ?? t("wachtlijstDossier.onbekendeSchrijver")}
                    {" · "}
                    {formatDateTime(n.aangemaaktOp, locale)}
                  </small>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {tab === "historie" && (
        <section className="wl-kaart">
          <h3>{t("wachtlijstDossier.tab.historie")}</h3>
          <ul className="wl-historie">
            <li>
              <FileText size={14} aria-hidden="true" />
              <span>{t("wachtlijstDossier.ontvangenOp")}</span>
              <time>{formatDateTime(regel.ontvangenOp, locale)}</time>
            </li>
            {regel.ingediendOp && (
              <li>
                <Check size={14} aria-hidden="true" />
                <span>{t("wachtlijstDossier.ingediendOp")}</span>
                <time>{formatDateTime(regel.ingediendOp, locale)}</time>
              </li>
            )}
          </ul>
          {/* Wat er daarna met het dossier gebeurt staat er nog niet in: er is nog geen logboek per
              aanvraag. Dat eerlijk zeggen is beter dan een lege lijst die compleet lijkt. */}
          <p className="fawkes-muted">{t("wachtlijstDossier.historieOnvolledig")}</p>
        </section>
      )}
    </div>
  );
}

/** De controle naast de dieren: wat er af is, als ring en als lijstje. */
function Dossiercontrole({ dossier, vragen, t }: {
  dossier: Dossier;
  vragen: { beantwoord: number; verplicht: number };
  t: (k: string, o?: { args: string[] }) => string;
}) {
  const contact = dossier.contact;
  const naw = [contact?.naam, contact?.email, contact?.telefoon, contact?.woonplaats];
  const nawOk = naw.filter((x) => x && x.trim()).length;

  // Eén getal uit twee tellingen: de gegevens en de vragen wegen even zwaar, want een dossier met
  // alle vragen beantwoord maar zonder telefoonnummer kun je net zo min afhandelen.
  const totaal = naw.length + vragen.verplicht;
  const gedaan = nawOk + vragen.beantwoord;
  const percentage = totaal === 0 ? 0 : Math.round((gedaan / totaal) * 100);

  return (
    <section className="wl-kaart wl-controle">
      <h3>{t("wachtlijstDossier.controle")}</h3>
      <div className="wl-controle__ring" style={{ ["--p" as string]: `${percentage}` }}>
        <span>{percentage}%</span>
      </div>
      <ul className="wl-controle__lijst">
        <Punt ok={nawOk === naw.length} tekst={t("wachtlijstDossier.controle.gegevens")}
              telling={`${nawOk}/${naw.length}`} />
        <Punt ok={vragen.verplicht > 0 && vragen.beantwoord === vragen.verplicht}
              tekst={t("wachtlijstDossier.controle.vragen")}
              telling={`${vragen.beantwoord}/${vragen.verplicht}`} />
      </ul>
    </section>
  );
}

function Punt({ ok, tekst, telling }: { ok: boolean; tekst: string; telling: string }) {
  return (
    <li className={ok ? "is-ok" : undefined}>
      <span className="wl-controle__bol" aria-hidden="true" />
      <span>{tekst}</span>
      <strong>{telling}</strong>
    </li>
  );
}
